import type { MouseEventHandler } from "react";

import type { Translate } from "@similarweb/i18n-provider";
import { Settings, ArrowRight } from "@similarweb/icons";
import type { MonitorAndProtectModelTrademarksTrademarkModel } from "@similarweb/pro-api-client";
import { ChipOverflowCell, tableSimpleSort } from "@similarweb/pro-logic-components";
import type { ColumnsType } from "@similarweb/pro-logic-components";
import { Tooltip } from "@similarweb/ui-components";
import { Flex } from "@similarweb/ui-components/foundations";

import { DateFormatter } from "app/pages/digital-marketing/common/services/formatters/dateFormatter";
import { getFaviconUrl } from "app/services/sitesService/sitesUtils";

import { mnpTrademarkNavigation } from "../../app/navigation";
import type { TrademarkPermissions } from "../../services/asset-permissions";
import { IconButton } from "../../shared/components/icon-button";
import { DomainCell } from "../../shared/ui/table/cells";

export type TrademarkTableRow = MonitorAndProtectModelTrademarksTrademarkModel & {
    resolvedPermissions: TrademarkPermissions;
};

const handleRedirect = (trademarkId: string) => {
    return function (e) {
        e.stopPropagation();
        void mnpTrademarkNavigation.trademarksEdit.go({ trademarkId });
    } as MouseEventHandler<HTMLButtonElement>;
};

const RedirectAction = ({ row, translate }: { row: TrademarkTableRow; translate?: Translate }) => {
    const canViewOnly = row.resolvedPermissions.canView && row.resolvedPermissions.canEdit === false;
    const tooltipMessage = canViewOnly
        ? "monitor_and_protect.features.trademark_config_list.view_details_tooltip"
        : "monitor_and_protect.features.trademark_config_list.edit_tooltip";

    return (
        <Tooltip content={translate ? translate(tooltipMessage) : tooltipMessage}>
            <IconButton onClick={handleRedirect(row.id)}>
                <Settings size="xs" />
            </IconButton>
        </Tooltip>
    );
};

export const getTableColumnConfig = (
    translate?: Translate,
    sortState?: { field: string; order: "ascend" | "descend" },
): ColumnsType<TrademarkTableRow> => [
    {
        title: translate?.("monitor_and_protect.features.trademark_config_list.table.trademark_column"),
        dataIndex: "name",
        sorter: tableSimpleSort("name"),
        sortOrder: sortState?.field === "name" ? sortState.order : undefined,
        render: (_val, row) => (
            <DomainCell
                title={row.name}
                data="0"
                iconUrl={getFaviconUrl(row.domain)}
                type="domain"
            />
        ),
    },
    {
        title: translate?.("monitor_and_protect.features.trademark_config_list.table.variations_column"),
        dataIndex: "variationsAndTypos",
        render: (val: string[]) => <ChipOverflowCell labels={val.map((text) => ({ text }))} />,
    },
    {
        title: translate?.("monitor_and_protect.features.trademark_config_list.table.date_added_column"),
        dataIndex: "createdAt",
        align: "right",
        sorter: tableSimpleSort("createdAt"),
        sortOrder: sortState?.field === "createdAt" ? sortState.order : undefined,
        render: (val) => DateFormatter.toShortDate(val as string),
    },
    {
        title: translate?.("monitor_and_protect.features.trademark_config_list.table.actions_column"),
        align: "right",
        render: (_, row) => (
            <Flex
                gap="8px"
                justifyContent="end"
            >
                <RedirectAction
                    row={row}
                    translate={translate}
                />
                <IconButton>
                    <ArrowRight size="xs" />
                </IconButton>
            </Flex>
        ),
    },
];
